import type {
  AddFieldPlacementTarget,
  AuthoredEntityOrSingleSchema,
  LoadedAuthoredGeneratedSchema
} from '@cli-types/core-engine/add-field.js'
import type { FormSchema } from '@cli-types/core-engine/schema.js'
import { buildPlacementTargets } from './build-placement-targets.js'
import { normalizeAddFieldSlot } from './normalize-add-field-slot.js'

export function resolveDefaultPlacementTarget(
  loaded: LoadedAuthoredGeneratedSchema,
  options: { slot?: string; step?: string; targetId?: string } = {}
): AddFieldPlacementTarget {
  const targets = buildPlacementTargets(loaded)
  const slot = normalizeAddFieldSlot(options.slot)

  if (options.targetId) {
    const target = targets.find((candidate) => candidate.id === options.targetId)
    if (!target) {
      throw new Error(`Placement target "${options.targetId}" was not found.`)
    }
    return target
  }

  if (loaded.kind === 'form') {
    if (slot) {
      throw new Error('--slot is only supported for Entity and Single schemas.')
    }

    const formSchema = loaded.schema as FormSchema
    if (formSchema.steps) {
      if (!options.step) {
        throw new Error('This form has steps. Use --step <name> to choose the target step.')
      }
      const target = targets.find((candidate) => candidate.id === `step:${options.step}`)
      if (!target) {
        throw new Error(`Form step "${options.step}" was not found.`)
      }
      return target
    }

    return targets.find((candidate) => candidate.id === 'root:fields') ?? targets[0]!
  }

  const schema = loaded.schema as AuthoredEntityOrSingleSchema
  if (schema.slot) {
    const targetSlot = slot ?? 'main'
    const target = targets.find((candidate) => candidate.id === `root:slot:${targetSlot}`)
    if (!target) {
      throw new Error(`Placement target "root:slot:${targetSlot}" was not found.`)
    }
    return target
  }

  const target = targets.find((candidate) => candidate.id === 'root:fields') ?? targets[0]!
  return {
    ...target,
    getFields: () => target.getFields()
  }
}
